import type { CarInput, CarState } from './car.js';
import { angleDelta, headingFromDirection, pointAlong, polylineLength, projectOntoPolyline } from './geom.js';

/**
 * A pure-pursuit controller that drives the race line.
 *
 * It exists because the end-to-end tests have to get two cars from the start to the
 * finish of a real city route, and doing that by synthesising thirty seconds of keyboard
 * events is both slow and hopelessly flaky. It is also genuinely useful as a demo mode —
 * and it is the obvious skeleton for an AI opponent, which v1 does not have.
 *
 * The technique is the standard one: look a fixed distance ahead along the path, steer at
 * that point, and back off the throttle in proportion to how hard you are having to turn.
 */

export interface AutopilotOptions {
  /** How far ahead on the path to aim, in metres. Longer = smoother but cuts corners. */
  lookahead?: number;
  /** Steering gain on the heading error. */
  gain?: number;
  /** Fraction of full throttle to use. Lower makes a more cautious driver. */
  pace?: number;
  /** Seconds since the last call. Only used by the stuck detector. */
  dt?: number;
}

/**
 * Per-car recovery state, kept outside the public signature so callers don't have to
 * thread it through. Keyed weakly on the car itself, so it costs nothing and disappears
 * with the car.
 */
interface Recovery {
  /** Seconds spent asking for power and not getting any. */
  stuckFor: number;
  /** Seconds left of the current escape manoeuvre. */
  manoeuvreFor: number;
  /** Throttle and steer to hold for the duration of it. */
  throttle: number;
  steer: number;
  /** How many escapes we've tried without getting moving again. */
  attempts: number;
}

const recoveries = new WeakMap<CarState, Recovery>();

/** Below this, with the throttle open, the car is against something. */
const STUCK_SPEED = 1.2;
/** How long to tolerate that before backing up. */
const STUCK_AFTER = 0.7;
/** How long to hold each escape manoeuvre before trying the other direction. */
const ESCAPE_FOR = 1.1;

/**
 * Lateral acceleration the autopilot will plan for, m/s². The handling is tuned to about
 * 1.3 g at full lock; this sits deliberately below that so it corners inside the limit
 * rather than on it.
 */
const LATERAL_GRIP = 9.5;
/** How far ahead to look for corners, and at what spacing, in metres. */
const CURVATURE_HORIZON = 46;
const CURVATURE_STEP = 6;

export function autopilotInput(
  car: CarState,
  path: number[],
  options: AutopilotOptions = {},
): CarInput {
  const gain = options.gain ?? 1.9;
  const pace = options.pace ?? 1;
  const speed = Math.hypot(car.vx, car.vz);
  // Look further ahead the faster you go, or the car saws at speed and dives at corners.
  const lookahead = options.lookahead ?? Math.min(46, 14 + speed * 1.1);

  const here = projectOntoPolyline(path, car.x, car.z);
  const total = polylineLength(path);
  const aim = pointAlong(path, Math.min(total, here.distance + lookahead));

  const dx = aim.x - car.x;
  const dz = aim.z - car.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 0.5) return { throttle: pace, steer: 0, handbrake: false };

  const desired = headingFromDirection(dx / dist, dz / dist);
  const error = angleDelta(car.heading, desired);

  // --- stuck detection ------------------------------------------------------
  // Pure pursuit aims at a point on the road ahead. When the car has nosed into a
  // building it will happily keep aiming *through* the wall — the heading error is near
  // zero, because the target really is dead ahead — and hold full throttle against it
  // forever. Heading error alone can never detect this; the only reliable signal is
  // "asking for power and not moving".
  const dt = options.dt ?? 1 / 60;
  let recovery = recoveries.get(car);
  if (!recovery) {
    recovery = { stuckFor: 0, manoeuvreFor: 0, throttle: 0, steer: 0, attempts: 0 };
    recoveries.set(car, recovery);
  }

  const moving = speed >= STUCK_SPEED;
  if (moving) {
    recovery.stuckFor = 0;
    recovery.attempts = 0;
    recovery.manoeuvreFor = 0;
  } else {
    recovery.stuckFor += dt;
  }

  if (recovery.manoeuvreFor > 0) {
    recovery.manoeuvreFor -= dt;
    // Abandon a manoeuvre that is itself going nowhere, rather than serving out its full
    // duration pressed against whatever is blocking it.
    if (recovery.stuckFor > STUCK_AFTER + ESCAPE_FOR) recovery.manoeuvreFor = 0;
    return { throttle: recovery.throttle, steer: recovery.steer, handbrake: false };
  }

  if (recovery.stuckFor > STUCK_AFTER) {
    // Alternate the direction on every attempt. The car that spun round and backed itself
    // against a wall is stuck *and* facing the wrong way, so "reverse out" — the obvious
    // escape — is the one direction that cannot work. Trying reverse then forward then
    // reverse guarantees we eventually push the way that is actually open, and flipping
    // the steering with it means we don't retrace the same arc into the same corner.
    recovery.attempts++;
    const backwards = recovery.attempts % 2 === 1;
    recovery.throttle = backwards ? -1 : 1;
    // Steer towards the road while going forwards, away from it while reversing, so both
    // halves of a three-point turn rotate the car the same way.
    const towards = clamp((Math.sign(error) || 1) * (backwards ? -1 : 1), -1, 1);
    recovery.steer = towards;
    recovery.manoeuvreFor = ESCAPE_FOR;
    recovery.stuckFor = STUCK_AFTER;
    return { throttle: recovery.throttle, steer: recovery.steer, handbrake: false };
  }

  // --- normal pursuit -------------------------------------------------------
  const steer = clamp(error * gain, -1, 1);

  // Brake for the corner *before* reaching it.
  //
  // Steering into the current heading error is far too late: the error stays near zero
  // right up until the car is in the junction, by which point a 25 m/s car has a 38 m
  // turning circle and a Manhattan corner needs about 12 m. It simply cannot make the
  // turn, so it ploughs into the building opposite — reliably, in the same place, every
  // run. Looking at the curvature of the road ahead and capping speed at what the tyres
  // can actually hold through it is the difference between a car that drives and a car
  // that crashes at the first junction.
  const kappa = curvatureAhead(path, here.distance, total);
  const cornerSpeed = kappa > 1e-4 ? Math.sqrt(LATERAL_GRIP / kappa) : Infinity;
  const strayFactor = here.d2 > 400 ? 0.65 : 1;

  let throttle = pace * strayFactor * (1 - Math.min(0.6, Math.abs(error) * 0.7));
  if (speed > cornerSpeed * 1.08) throttle = -1;
  else if (speed > cornerSpeed * 0.92) throttle = Math.min(throttle, 0.15);

  // Facing more or less backwards: reverse rather than swinging a wide U-turn.
  if (Math.abs(error) > 2.2) throttle = -0.6;
  // Crawling but not yet judged stuck — make sure there's enough power to pull away.
  else if (speed < 1 && throttle < 0.5) throttle = 0.5;

  return { throttle: clamp(throttle, -1, 1), steer, handbrake: false };
}

/**
 * Peak curvature (1/radius, in 1/m) of the road over the next ~45 m.
 *
 * Sampled rather than differentiated, because the route polyline is simplified to a 1 m
 * tolerance and its vertex spacing is wildly uneven — a true derivative is dominated by
 * whichever two nodes happen to be closest together.
 */
function curvatureAhead(path: number[], from: number, total: number): number {
  let peak = 0;
  let previous: { x: number; z: number } | null = null;
  let previousHeading: number | null = null;

  for (let ahead = 0; ahead <= CURVATURE_HORIZON; ahead += CURVATURE_STEP) {
    const point = pointAlong(path, Math.min(total, from + ahead));
    if (previous) {
      const dx = point.x - previous.x;
      const dz = point.z - previous.z;
      const length = Math.hypot(dx, dz);
      if (length > 0.5) {
        const heading = Math.atan2(dz, dx);
        if (previousHeading !== null) {
          // Turn angle over the distance travelled is curvature, by definition.
          const turn = Math.abs(angleDelta(previousHeading, heading));
          peak = Math.max(peak, turn / length);
        }
        previousHeading = heading;
      }
    }
    previous = point;
  }
  return peak;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
